<script setup lang="ts">
import type {
	BehaviorConfig,
	LocationDataConfig,
	StyleConfig,
	Suggestion,
	SuggestionCondoResult
} from './types';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { FieldType } from '~/composables/useSearchSuggestNavigation';
import type { CurrentPage } from '~/types/mixpanel';

import { useEvents } from '~/composables/events/useEvents';
import { useResearchsSuggest } from '~/composables/useResearchsSuggest';
import {
	FieldTypeEnum,
	useSearchSuggestNavigation
} from '~/composables/useSearchSuggestNavigation';
import { useTwoFieldSuggest } from '~/composables/useTwoFieldSuggest';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';

import FirstFieldSuggest from './FirstFieldSuggest.vue';
import FirstFieldSuggestDialog from './FirstFieldSuggestDialog.vue';
import SecondFieldSuggest from './SecondFieldSuggest.vue';
import SecondFieldSuggestDialog from './SecondFieldSuggestDialog.vue';

interface TwoFieldSuggestProps {
	locationData?: LocationDataConfig;
	styleConfig?: StyleConfig;
	behaviorConfig?: BehaviorConfig;
	firstFieldClasses?: string;
	secondFieldClasses?: string;
	useDialog?: boolean;
	regions?: LocationDataConfig['regions'];
	cities?: LocationDataConfig['cities'];
	macroregions?: LocationDataConfig['macroregions'];
	currentPage?: CurrentPage;
}

const props = withDefaults(defineProps<TwoFieldSuggestProps>(), {
	locationData: () => ({}),
	styleConfig: () => ({}),
	behaviorConfig: () => ({
		enableAnimation: true,
		closeOnClickOutside: true,
		closeOnSelect: true,
		showCloseIcon: false
	}),
	useDialog: false
});

const emit = defineEmits<{
	(e: 'select-address', event: SelectAddressEvent, fieldType: FieldType): void;
	(e: 'select-place', event: SelectAddressEvent, fieldType: FieldType): void;
	(e: 'select-condo', value: SuggestionCondoResult, fieldType: FieldType): void;
	(
		e: 'select-code',
		value: Suggestion['searchParams'],
		fieldType: FieldType
	): void;
	(e: 'first-field-selected', event: SelectAddressEvent): void;
	(e: 'second-field-enabled-change', isEnabled: boolean): void;
	(e: 'close-modal'): void;
}>();

// Merge props antigas com configs novas para compatibilidade
const mergedLocationData = computed<LocationDataConfig>(() => ({
	regions: props.locationData?.regions || props.regions,
	cities: props.locationData?.cities || props.cities,
	macroregions: props.locationData?.macroregions || props.macroregions
}));

// Merge first field classes with style config
const firstFieldStyleConfig = computed<StyleConfig>(() => ({
	...props.styleConfig,
	inputWrapperClasses: [
		props.styleConfig.inputWrapperClasses,
		props.firstFieldClasses
	]
		.filter(Boolean)
		.join(' ')
}));

// Merge second field classes with style config
const secondFieldStyleConfig = computed<StyleConfig>(() => ({
	...props.styleConfig,
	inputWrapperClasses: [
		props.styleConfig.inputWrapperClasses,
		props.secondFieldClasses
	]
		.filter(Boolean)
		.join(' ')
}));

// Estado compartilhado entre os dois campos
const {
	firstFieldSelection,
	firstFieldSearchTerm,
	isSecondFieldEnabled,
	selectedBoundingRegion,
	selectedMacroregion
} = useTwoFieldSuggest();

function handleFirstFieldUpdateSearchTerm(searchTerm: string) {
	firstFieldSearchTerm.value = searchTerm;
}

// Navegação
const {
	handleSelectCode,
	handleSelectAddress,
	handleSelectCondo,
	handleSelectPlace
} = useSearchSuggestNavigation({ currentPage: props.currentPage });

// Estado de loading para busca por código
const isFetchingPropertyByCodeSearch = ref(false);

// Dialog state for mobile
const isFirstFieldDialogOpen = ref(false);
const isSecondFieldDialogOpen = ref(false);

const { filteredLastResearchs } = useResearchsSuggest();

const { trackSearchHistoryFilter } = useEvents();

function trackHistorySelection(suggestion: Suggestion, isEmptyState: boolean) {
	const historyItems = filteredLastResearchs.value;
	const position =
		historyItems.findIndex(item => item.id === suggestion.id) + 1;
	const otherItems = historyItems.filter(item => item.id !== suggestion.id);
	const suggestionName = suggestion.result?.title || '';
	trackSearchHistoryFilter({
		[MixpanelProps.SUGGESTION_CLICKED]: suggestionName,
		[MixpanelProps.SUGGESTION_POSITION]: position,
		[MixpanelProps.SUGGEST_CLICK]: !isEmptyState,
		[MixpanelProps.EMPTY_STATE_CLICK]: isEmptyState,
		[MixpanelProps.OTHER_SUGGESTIONS_COUNT]: otherItems.length,
		[MixpanelProps.OTHER_SUGGESTIONS]: otherItems
			.map(item => item.result?.title || '')
			.join(', ')
	});
}

/**
 * Trata seleção de código no primeiro campo
 * Navega diretamente para PDP ou fallback
 */
async function handleFirstFieldSelectCode(event: Suggestion['searchParams']) {
	await handleSelectCode(event, FieldTypeEnum.FIRST);
}

/**
 * Trata seleção de itens do histórico no primeiro campo
 * Direciona baseado no tipo de busca do histórico
 */
function handleFirstFieldSelectHistory(
	suggestion: Suggestion,
	isEmptyState: boolean = false
) {
	trackHistorySelection(suggestion, isEmptyState);

	const { searchType, searchParams, macroregionUsed } = suggestion;

	switch (searchType) {
		case 'code':
			handleFirstFieldSelectCode(searchParams);
			break;
		case 'condo':
			handleSelectCondo(
				searchParams as SuggestionCondoResult,
				FieldTypeEnum.FIRST
			);
			break;
		case 'region':
		case 'city':
		case 'macroregion':
			// Update firstFieldSelection with location data for macroregion filtering
			firstFieldSelection.value = {
				macroregions: searchParams.macroregions,
				cities: searchParams.cities,
				regions: searchParams.regions,
				states: searchParams.states,
				lat: searchParams.lat,
				lng: searchParams.lng,
				placeId: searchParams.placeId,
				condoSlug: searchParams.condoSlug,
				code: searchParams.code,
				searchType,
				macroregionUsed
			};
			// Para regiões/cidades: NÃO passar text (não deve aparecer na URL)
			handleSelectAddress(
				{
					macroregions: searchParams.macroregions,
					cities: searchParams.cities,
					regions: searchParams.regions,
					states: searchParams.states,
					lat: searchParams.lat,
					lng: searchParams.lng,
					placeId: searchParams.placeId,
					condoSlug: searchParams.condoSlug,
					code: searchParams.code,
					searchType,
					macroregionUsed
				},
				FieldTypeEnum.FIRST
			);
			break;
		case 'street':
		case 'point_of_interest':
			// Para endereços/POIs: update location info but don't set for search URL
			firstFieldSelection.value = {
				macroregions: searchParams.macroregions || [],
				cities: searchParams.cities || [],
				regions: searchParams.regions || [],
				states: searchParams.states || ['sp'],
				lat: searchParams.lat,
				lng: searchParams.lng,
				text: searchParams.text,
				placeId: searchParams.placeId,
				condoSlug: searchParams.condoSlug,
				code: searchParams.code,
				searchType,
				macroregionUsed
			};
			handleSelectPlace(
				{
					macroregions: searchParams.macroregions,
					cities: searchParams.cities,
					regions: searchParams.regions,
					states: searchParams.states || ['sp'],
					lat: searchParams.lat,
					lng: searchParams.lng,
					text: searchParams.text,
					placeId: searchParams.placeId,
					condoSlug: searchParams.condoSlug,
					code: searchParams.code,
					searchType: searchType as SelectAddressEvent['searchType']
				},
				FieldTypeEnum.FIRST
			);
			break;
	}
}

/**
 * Trata seleção de bairro no segundo campo
 */
function handleSecondFieldSelectNeighborhood(event: SelectAddressEvent) {
	handleSelectAddress(event, FieldTypeEnum.SECOND);
}

/**
 * Trata seleção de endereço no segundo campo
 */
function handleSecondFieldSelectStreet(event: SelectAddressEvent) {
	selectedMacroregion.value = event.macroregionUsed;
	handleSelectPlace(event, FieldTypeEnum.SECOND);
}

/**
 * Trata seleção de POI no segundo campo
 */
function handleSecondFieldSelectPOI(event: SelectAddressEvent) {
	selectedMacroregion.value = event.macroregionUsed;
	handleSelectPlace(event, FieldTypeEnum.SECOND);
}

/**
 * Trata seleção de condomínio no segundo campo
 */
function handleSecondFieldSelectCondo(params: SuggestionCondoResult) {
	handleSelectCondo(params, FieldTypeEnum.SECOND);
}

/**
 * Trata seleção de código no segundo campo
 * Com fallback inteligente para a região selecionada
 */
async function handleSecondFieldSelectCode(params: Suggestion['searchParams']) {
	if (!params.code) return;

	const code = params.code;
	isFetchingPropertyByCodeSearch.value = true;

	// Yield to browser so loading state paints before the API call
	await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

	try {
		const [property, error] = await nuxtPropertiesRepository.checkOne(code);

		if (!property || error) {
			// Fallback para SRP da região selecionada (não o fallback geral)
			const region = firstFieldSelection.value;
			if (region) {
				const filters = PropertyFiltersPresenter.createLocationFilters({
					states: region.macroregions,
					cities: region.cities,
					regions: region.regions
				});
				const path = PropertyFiltersPresenter.getPathFromFilters(filters);
				navigateTo(path);
			} else {
				// Fallback padrão só se não houver região
				navigateTo(PropertyFiltersPresenter.DEFAULT_URL_PATH);
			}
			return;
		}

		// Navega para PDP
		navigateTo(`/imovel/${property.commercialId}/${property.slug}`);
	} catch (error) {
		console.error('Error fetching property by code:', error);
		// Fallback
		const region = firstFieldSelection.value;
		if (region) {
			const filters = PropertyFiltersPresenter.createLocationFilters({
				states: region.macroregions,
				cities: region.cities,
				regions: region.regions
			});
			const path = PropertyFiltersPresenter.getPathFromFilters(filters);
			navigateTo(path);
		} else {
			navigateTo(PropertyFiltersPresenter.DEFAULT_URL_PATH);
		}
	} finally {
		isFetchingPropertyByCodeSearch.value = false;
	}
}

/**
 * Trata seleção de itens do histórico no segundo campo
 */
function handleSecondFieldSelectHistory(
	suggestion: Suggestion,
	isEmptyState: boolean = false
) {
	trackHistorySelection(suggestion, isEmptyState);

	const { searchType, searchParams, macroregionUsed } = suggestion;

	switch (searchType) {
		case 'code':
			handleSecondFieldSelectCode(searchParams);
			break;
		case 'condo':
			handleSelectCondo(
				searchParams as SuggestionCondoResult,
				FieldTypeEnum.SECOND
			);
			break;
		// Para regiões/bairros: NÃO passar text (não deve aparecer na URL)
		case 'region':
		case 'city':
		case 'macroregion':
			handleSecondFieldSelectNeighborhood({
				macroregions: searchParams.macroregions || [],
				cities: searchParams.cities || [],
				regions: searchParams.regions || [],
				lat: searchParams.lat,
				lng: searchParams.lng,
				placeId: searchParams.placeId,
				condoSlug: searchParams.condoSlug,
				code: searchParams.code,
				searchType,
				macroregionUsed
			});
			break;
		case 'street':
			// Para endereços: passar text com arrays vazios para região
			handleSecondFieldSelectStreet({
				macroregions: [],
				cities: [],
				regions: [],
				states: searchParams.states || ['sp'],
				lat: searchParams.lat,
				lng: searchParams.lng,
				text: searchParams.text,
				placeId: searchParams.placeId,
				condoSlug: searchParams.condoSlug,
				code: searchParams.code,
				searchType,
				macroregionUsed
			});
			break;
		case 'point_of_interest':
			// Para POIs: passar text com arrays vazios para região
			handleSecondFieldSelectPOI({
				macroregions: [],
				cities: [],
				regions: [],
				states: searchParams.states || ['sp'],
				lat: searchParams.lat,
				lng: searchParams.lng,
				text: searchParams.text,
				placeId: searchParams.placeId,
				condoSlug: searchParams.condoSlug,
				code: searchParams.code,
				searchType,
				macroregionUsed
			});
			break;
	}
}

/**
 * Trata fechamento de modal
 */
function handleCloseModal() {
	emit('close-modal');
}

/**
 * Watcher para emitir evento quando o primeiro campo é selecionado
 */
watch(
	firstFieldSelection,
	newSelection => {
		if (!newSelection) return;
		emit('first-field-selected', newSelection);
	},
	{ deep: true }
);

/**
 * Watcher para emitir evento quando o segundo campo é habilitado/desabilitado
 */
watch(isSecondFieldEnabled, isEnabled => {
	emit('second-field-enabled-change', isEnabled);
});
</script>

<template>
	<div class="flex flex-col place-self-center">
		<!-- Dialog Version (Mobile) -->
		<template v-if="useDialog">
			<!-- Primeiro Campo: Regiões, Cidades e Códigos -->
			<FirstFieldSuggestDialog
				v-model:is-open="isFirstFieldDialogOpen"
				v-model:selected-location="firstFieldSelection"
				:location-data="mergedLocationData"
				:style-config="firstFieldStyleConfig"
				:behavior-config="behaviorConfig"
				@update:search-term="handleFirstFieldUpdateSearchTerm"
				@select-code="handleFirstFieldSelectCode"
				@select-history="handleFirstFieldSelectHistory"
				@close-modal="handleCloseModal"
			/>

			<!-- Segundo Campo: Bairros, Endereços, POIs e Condomínios -->
			<SecondFieldSuggestDialog
				v-model:is-open="isSecondFieldDialogOpen"
				:disabled="!isSecondFieldEnabled"
				:has-first-field-value="!!firstFieldSelection"
				:first-field-search-term="firstFieldSearchTerm"
				:city="selectedBoundingRegion?.cities?.[0]"
				:state="selectedBoundingRegion?.states?.[0]"
				:is-fetching-property-by-code-search="isFetchingPropertyByCodeSearch"
				:style-config="secondFieldStyleConfig"
				:behavior-config="behaviorConfig"
				@select-neighborhood="handleSecondFieldSelectNeighborhood"
				@select-street="handleSecondFieldSelectStreet"
				@select-poi="handleSecondFieldSelectPOI"
				@select-condo="handleSecondFieldSelectCondo"
				@select-code="handleSecondFieldSelectCode"
				@select-history="handleSecondFieldSelectHistory"
				@close-modal="handleCloseModal"
			/>
		</template>

		<!-- Non-Dialog Version (Desktop) -->
		<template v-else>
			<!-- Primeiro Campo: Regiões, Cidades e Códigos -->
			<FirstFieldSuggest
				v-model:selected-location="firstFieldSelection"
				:location-data="mergedLocationData"
				:style-config="firstFieldStyleConfig"
				:behavior-config="behaviorConfig"
				@select-code="handleFirstFieldSelectCode"
				@update:search-term="handleFirstFieldUpdateSearchTerm"
				@select-history="handleFirstFieldSelectHistory"
			/>

			<!-- Segundo Campo: Bairros, Endereços, POIs e Condomínios -->
			<SecondFieldSuggest
				:disabled="!isSecondFieldEnabled"
				:has-first-field-value="!!firstFieldSelection"
				:first-field-search-term="firstFieldSearchTerm"
				:city="selectedBoundingRegion?.cities?.[0]"
				:state="selectedBoundingRegion?.states?.[0]"
				:is-fetching-property-by-code-search="isFetchingPropertyByCodeSearch"
				:style-config="secondFieldStyleConfig"
				:behavior-config="behaviorConfig"
				@select-neighborhood="handleSecondFieldSelectNeighborhood"
				@select-street="handleSecondFieldSelectStreet"
				@select-poi="handleSecondFieldSelectPOI"
				@select-condo="handleSecondFieldSelectCondo"
				@select-code="handleSecondFieldSelectCode"
				@select-history="handleSecondFieldSelectHistory"
				@close-modal="handleCloseModal"
			/>
		</template>
	</div>
</template>
